// Expose core modules on the window scope to avoid importing them everywhere
import 'expose-loader?angular!angular';
import 'expose-loader?_!lodash';
import 'expose-loader?moment!moment';
import './utils/polyfill';
import './browser-hacks';

// Throw config on window scope as well
// eslint-disable-next-line no-unused-vars
import ENV from './config';

// Check authentication token from OAuth redirect straight away
import { bootstrapOAuthTokenExchange } from './auth/bootstrap';
import { isSwitcherStudioAppShell } from './auth/utils';
var tokenPromise = bootstrapOAuthTokenExchange();

// Inject Bootstrap CSS via webpack loaders
import 'normalize.css';

// Create the core app angular module, depending on our sub-modules
import authModule from './auth/_index';
import directivesModule from './directives/_index';
import filtersModule from './filters/_index';
import pagesModule from './pages/_index';
import resourcesModule from './resources/_index';
import servicesModule from './services/_index';
import thirdpartyModule from './thirdparty';

let app = angular.module('app', [
  authModule.name,
  directivesModule.name,
  filtersModule.name,
  pagesModule.name,
  resourcesModule.name,
  servicesModule.name,
  thirdpartyModule.name,
]);

// Main app layout
import './styles/main.scss';
app.directive('app', () => {
  'ngInject';
  return {
    restrict: 'E',
    template: require('./app.html'),
    controllerAs: 'vm',
    controller: function (Capabilities, $rootScope, $timeout) {
      'ngInject';

      // Handle race condition of double feature flags initialization (we don't want $stateChangeStart
      // to fire until *after* its handler's Capabilities are initialized)
      this.loading = true;
      Capabilities.then(() => {
        $timeout(() => {
          // Prevent Go plan users from accessing the dashboard
          // TODO: This is fragile. We should use a feature flag or explicit
          //       plan names to determine Dashboard access.
          const isFreePlan = Capabilities.account.contract === 'free';
          const maxMinutes = Capabilities.account.max_streamed_minutes_per_month;
          const isGoPlan = isFreePlan && maxMinutes > 0 && maxMinutes <= 3 * 60;
          if (isGoPlan) {
            window.location.href = 'https://www.boxcast.com/go-plan/gated';
            return;
          }

          // Account and User were already retrieved by way of Capabilities
          // Pluck them from there to avoid double-requesting.
          this.account = Capabilities.account;
          this.user = Capabilities.user;
          this.Capabilities = Capabilities;

          // Initialize DataDog logging
          remoteLogger.addContext('service', 'dashboard');
          remoteLogger.addContext('env', BOXCAST_JS_ENV);
          remoteLogger.addContext('account_id', this.account.id);
          remoteLogger.addContext('user_id', this.user.id);

          // Load the chat widget
          if (this.account.chat_support && !this.user.is_impersonating) {
            $rootScope.hasChat = true;
            loadChatWidget(this.user, this.account);
          }
          // use feature flag product_tour [UA-1023]
          if (this.account.extra_features.product_tour) {
            initializeAppcues(this.user, this.account);
            initializeMixpanel(this.user);
          }

          this.loading = false;
        }, 10);
      });

      // App layout depends on whether the eyebrow is shown or not
      $rootScope.$on('show-eyebrow', () => {
        this.showEyebrow = true;
      });
      $rootScope.$on('hide-eyebrow', () => {
        this.showEyebrow = false;
      });
    },
  };
});

function loadChatWidget(user, account) {
  if (!window.Intercom) {
    console.warn('Intercom not properly loaded');
    return;
  }
  const convertToSeconds = (date) => {
    if (date) {
      const newDate = new Date(date);
      return Math.floor(newDate / 1000);
    }
    return null;
  };
  window.Intercom('boot', {
    app_id: INTERCOM_APP_ID,
    user_id: user.id,
    email: user.email,
    name: user.name,
    company: {
      company_id: account.id,
      name: account.name,
    },
    trial_milestone_stage: account.metadata.onboarding_step || null,
    trial_expiration_at: convertToSeconds(account.expires_on),
    account_status: account.status,
    can_stream: account.can_stream,
    has_streamed: account.has_streamed,
    market: account.market || null,
    contract_status: account.contract,
  });
}

function initializeAppcues(user, account) {
  if (!window.Appcues) {
    console.warn('Appcues not properly loaded');
    return;
  }
  window.Appcues.identify(user.id, {
    email: user.email,
    name: user.name,
    can_stream: account.can_stream,
    has_streamed: account.has_streamed,
    trial_milestone_stage: account.metadata.onboarding_step || null,
    company_name: account.name,
    company_id: account.id,
    market: account.market || null,
    contract_status: account.contract,
    account_status: account.status,
  });
  window.Appcues.group(account.id, {
    company_name: account.name,
  });
}
function initializeMixpanel({ id }) {
  if (!window.mixpanel) {
    console.warn('mixpanel not properly loaded');
    return;
  }
  window.mixpanel.init('0ab50e682300f5e06a239867570871a1', { debug: true });
  window.mixpanel.identify(id);
}

app.config(
  ($urlRouterProvider, $stateProvider, $analyticsProvider, $compileProvider, ChartJsProvider) => {
    'ngInject';
    // Init UI-router
    $urlRouterProvider.otherwise('/?p=1,1,1&tab=current'); // go to home if route not found
    // Google Analytics
    $analyticsProvider.virtualPageviews(false); // explicitly track pages
    $analyticsProvider.firstPageview(false); // necessary for manual bootstrapping code
    $analyticsProvider.trackExceptions(true);

    // White-list blobs and other friendly URLs
    $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|mailto|tel|blob):/);

    // Configure all charts
    ChartJsProvider.setOptions({
      chartColors: [
        // Based on Stephen Few’s book, Show Me the Numbers
        '#5DA5DA', //blue
        '#4D4D4D', //gray
        '#FAA43A', //orange
        '#60BD68', //green
        '#F17CB0', //pink
        '#B2912F', //brown
        '#B276B2', //purple
        '#DECF3F', //yellow
        '#F15854', //red
      ],
      responsive: true,
      legend: {
        display: true,
        position: 'bottom',
        labels: { boxWidth: 10, fontSize: 10 },
      },
    });
    ChartJsProvider.setOptions('Line', {
      datasetFill: false,
    });
  }
);

var alreadyRan = false;
app.run(
  ($rootScope, $urlRouter, toaster, $state, $q, $analytics, clipboard, ApiConfig, Capabilities) => {
    'ngInject';

    // Attach some template helpers so we don't have to go through a controller/directive injection
    $rootScope.$state = $state;
    $rootScope._ = _;
    $rootScope.toaster = toaster;
    $rootScope.clipboard = clipboard;
    $rootScope.browser = {
      iPhone: navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPod/i),
    };
    $rootScope.vueDashboardRoot = '';
    if (window.location.hostname === 'localhost') {
      $rootScope.vueDashboardRoot = 'http://localhost:8001';
    }

    // Dealing with a little bit of a race condition here in the app startup lifecycle
    var loaded = false;
    Capabilities.then(() => {
      // Let other templates access a few important instances directly
      $rootScope.ApiConfig = ApiConfig;
      $rootScope.account = Capabilities.account;
      $rootScope.reseller = Capabilities.reseller;
      $rootScope.user = Capabilities.user;
      $rootScope.$state = $state;
      loaded = true;

      // Google Analytics custom dimensions are registered in GA Settings > Custom Variables
      try {
        ga('set', 'dimension1', $rootScope.account.market);
        ga('set', 'dimension2', $rootScope.account.contract);
      } catch (e) {
        console.log('Unable to set GA dimensions: ', e);
      }
    });

    if (!alreadyRan) {
      alreadyRan = true;

      $rootScope.$on('$locationChangeSuccess', (evt, newUrl, oldUrl) => {
        // Any time user succesfully navigates, scroll up and close mobile menu
        if (allowAutoScrollOnPageTransition(oldUrl, newUrl)) {
          window.scrollTo(0, 0);
        }

        $('left-navbar').parent().removeClass('nav-open');
        $('body').css({ overflow: 'auto' });
        // Also log the event in GA according to the route name rather than full URL
        $analytics.pageTrack($state.$current.name);
        // Also update Intercom
        window.Intercom && window.Intercom('update');
        // Also update Appcues [UA-1023]
        if (window.Appcues) {
          window.Appcues.page();
        }
      });

      $rootScope.$on('$stateChangeStart', (event, toState, toParams, fromState, fromParams) => {
        if (
          $state.current.confirmOnExit &&
          !confirm('There are unsaved changes. Are you sure you wish to leave this page?')
        ) {
          console.log('Unsaved changes; preventing default');
          event.preventDefault();
        }
        if (!loaded) {
          // Flags not fully loaded, need to wait a tick and try again
          event.preventDefault();
          Capabilities.then(() => {
            $state.go(toState.name, toParams);
          });
        } else if (!Capabilities.canUserVisitState(toState)) {
          console.log('User blocked from visiting state');
          event.preventDefault();
          if (!Capabilities.account.authorized()) {
            // Redirect unauthorized users to home page
            $state.go('home');
          }
        }
      });
    }
  }
);

function allowAutoScrollOnPageTransition(fromUrl, toUrl) {
  // By default, we always want to autoscroll.  There may be specific cases
  // where we don't (e.g. navigating in-page between channels and updating IDs)
  if (fromUrl.match(/\/channels/) && toUrl.match(/\/channels\?id=/)) {
    return false;
  }
  return true;
}

// Custom bootstrapping code so we can wait on required network resources before
// spinning up any angular code at all.
function loadTheApp() {
  const rootElement = angular.element(document);
  app.provider({
    $rootElement: function () {
      this.$get = () => rootElement;
    },
  });
  const preBootstrapInjector = angular.injector(['ng', 'app']);
  const $q = preBootstrapInjector.get('$q');
  const ApiConfig = preBootstrapInjector.get('ApiConfig');
  const Capabilities = preBootstrapInjector.get('Capabilities');

  $q.all([ApiConfig.verifyResolved(), Capabilities.verifyResolved()])
    .then(() => {
      angular.bootstrap(rootElement, ['app']);
    })
    .catch((err) => {
      showGlobalError(err);
    });
}

function showGlobalError(err) {
  console.error(err);
  $('body > app').hide();
  setTimeout(() => {
    $('#global-error-message').fadeIn();
    remoteLogger.error(`unable to load dashboard: ${err}`, { err, isSwitcherStudioAppShell });
  }, 1000); // delay a bit in case the error is masked by redirect for auth logic
}

if (tokenPromise) {
  tokenPromise.then(loadTheApp).catch((err) => {
    showGlobalError(err);
  });
} else {
  loadTheApp();
}

export default app;



// WEBPACK FOOTER //
// ./app/app.js